<template>
  <Dialog
    title="自定义显示列"
    :visible="visible"
    width="980px"
    @close="handleClose"
  >
    <div class="dialog-body">
      <div class="option-panel">
        <div
          v-for="option in options"
          :key="option.key"
          class="group"
        >
          <Checkbox
            v-model="option.isCheckAll"
            :indeterminate="option.isIndeterminate"
            @change="handleCheckAllChange($event, option)"
          >
            <span class="group-name">{{ option.name }}</span>
          </Checkbox>

          <div class="group-divider" />

          <div style="display: flex; flex-wrap: wrap;">
            <Checkbox
              v-for="column in option.children"
              :key="column.key"
              v-model="column.isSelected"
              :label="column.key"
              class="checkbox"
              @change="handleChange($event, column, option)"
            >
              {{ column.name }}
            </Checkbox>
          </div>
        </div>
      </div>

      <div class="selected-panel">
        <div class="selected-panel__header">
          <div class="selected-panel__summary">
            已选列数
            <span class="selected-panel__count">{{ selectedColumns.length }}</span>
          </div>

          <Button
            type="text"
            class="selected-panel__clear"
            :disabled="selectedColumns.length === 0"
            @click="handleClearSelected"
          >
            清空所有已选
          </Button>
        </div>

        <div class="selected-panel__content">
          <div
            v-if="selectedColumns.length === 0"
            class="selected-empty"
          >
            暂无已选列
          </div>

          <template v-else>
            <div
              v-for="type in selectedTypes"
              :key="type"
              class="selected-group"
            >
              <div class="selected-group__header">
                <div class="selected-group__title">
                  {{ selectedTypeLabels[type] }}
                </div>
                <div class="selected-group__count">
                  {{ selectedColumnsByType[type].length }}
                </div>
              </div>

              <div
                class="selected-group__body"
                @dragover.self="handleSelectedContainerDragOver(type, $event)"
                @drop.self="handleSelectedContainerDrop(type, $event)"
              >
                <div
                  v-if="selectedColumnsByType[type].length === 0"
                  class="selected-empty selected-empty--group"
                >
                  该分组暂无已选列
                </div>

                <template v-else>
                  <div
                    v-for="column in selectedColumnsByType[type]"
                    :key="column.key"
                    :class="[
                      'selected-item',
                      {
                        'is-dragging': draggingKey === column.key,
                        'is-drag-over': dragOverKey === column.key && draggingKey !== column.key,
                      },
                    ]"
                    draggable="true"
                    @dragstart="handleSelectedDragStart($event, column)"
                    @dragover="handleSelectedDragOver($event, column)"
                    @drop="handleSelectedDrop($event, column)"
                    @dragend="handleSelectedDragEnd"
                  >
                    <span class="selected-item__drag">::</span>

                    <div class="selected-item__text">
                      <div class="selected-item__name">{{ column.name }}</div>
                      <!-- <div class="selected-item__group">{{ column.groupName }}</div> -->
                    </div>

                    <button
                      type="button"
                      class="selected-item__remove"
                      title="移除"
                      draggable="false"
                      @mousedown.stop
                      @click.stop="handleRemoveSelected(column)"
                      @dragstart.stop.prevent
                    >
                      x
                    </button>
                  </div>
                </template>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <span
      slot="footer"
      class="footer"
    >
      <Button @click="handleCancel">取消</Button>
      <Button
        type="primary"
        @click="handleConfirm"
      >
        确定
      </Button>
    </span>
  </Dialog>
</template>

<script>
import { Dialog, Button, Checkbox } from 'element-ui';
import { cloneDeep } from 'lodash';

const MAX_ORDER = Number.MAX_SAFE_INTEGER;
const SELECTED_TYPES = ['dimension', 'metric'];

export default {
  components: {
    Dialog,
    Button,
    Checkbox,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },

    // 选项
    dataSource: {
      type: Array,
      default() {
        return [];
      },
    },

    baseDataSource: {
      type: Array,
      default() {
        return [];
      },
    },
  },

  data() {
    return {
      options: [],
      selectedOrderKeysByType: this.getEmptySelectedOrderKeys(),
      draggingKey: '',
      draggingType: '',
      dragOverKey: '',
      selectedTypes: SELECTED_TYPES.slice(),
      selectedTypeLabels: {
        dimension: '维度',
        metric: '指标',
      },
    };
  },

  computed: {
    selectedColumns() {
      const selectedColumnsByType = this.selectedColumnsByType;

      return this.selectedTypes.reduce((prev, type) => {
        return prev.concat(selectedColumnsByType[type] || []);
      }, []);
    },

    selectedColumnsByType() {
      const selectedMap = this.getSelectedColumns(this.options).reduce(
        (prev, current) => {
          prev[current.key] = current;
          return prev;
        },
        {},
      );

      return this.selectedTypes.reduce((prev, type) => {
        prev[type] = (this.selectedOrderKeysByType[type] || [])
          .map((key) => selectedMap[key])
          .filter(Boolean);
        return prev;
      }, this.getEmptySelectedOrderKeys());
    },
  },

  watch: {
    visible(val) {
      if (val) {
        this.resetOptions(this.dataSource);
      }
    },

    dataSource: {
      deep: true,
      handler(val) {
        if (!this.visible) {
          this.resetOptions(val);
        }
      },
    },
  },

  created() {
    this.resetOptions(this.dataSource);
  },

  methods: {
    resetOptions(data) {
      const options = this.init(this.mergeOptionsWithBase(data));
      this.options = options;
      this.selectedOrderKeysByType = this.getInitialSelectedOrderKeys(options);
      this.draggingKey = '';
      this.draggingType = '';
      this.dragOverKey = '';
    },

    init(data) {
      const source = Array.isArray(data) ? data : [];
      return source.map((item) => {
        const children = Array.isArray(item.children) ? item.children : [];
        item.children = children;
        this.updateGroupCheckState(item);
        return item;
      });
    },

    mergeOptionsWithBase(data) {
      const savedOptions = Array.isArray(data) ? cloneDeep(data) : [];
      const baseOptions =
        Array.isArray(this.baseDataSource) && this.baseDataSource.length > 0
          ? cloneDeep(this.baseDataSource)
          : cloneDeep(savedOptions);

      const childStateMap = this.getColumnStateMap(savedOptions);

      return baseOptions.map((option) => {
        const children = Array.isArray(option.children) ? option.children : [];

        option.children = children.map((column) => {
          const columnState = childStateMap[column.key];

          if (columnState) {
            column.isSelected = columnState.isSelected !== false;

            if (Number.isFinite(Number(columnState.selectedOrder))) {
              column.selectedOrder = Number(columnState.selectedOrder);
            } else {
              delete column.selectedOrder;
            }
          } else {
            column.isSelected = false;
            delete column.selectedOrder;
          }

          return column;
        });

        return option;
      });
    },

    updateGroupCheckState(option) {
      this.$set(option, 'isCheckAll', this.isCheckAll(option.children || []));
      this.$set(
        option,
        'isIndeterminate',
        this.isIndeterminate(option.children || []),
      );
    },

    isFlatSelectedColumns(list) {
      return (
        Array.isArray(list) &&
        list.some((item) => !Array.isArray(item.children))
      );
    },

    getColumnStateMap(list = []) {
      if (!Array.isArray(list)) {
        return {};
      }

      if (this.isFlatSelectedColumns(list)) {
        return list.reduce((prev, column, index) => {
          prev[column.key] = {
            ...column,
            isSelected: column.isSelected !== false,
            selectedOrder: Number.isFinite(Number(column.selectedOrder))
              ? Number(column.selectedOrder)
              : index,
          };
          return prev;
        }, {});
      }

      return list.reduce((prev, option) => {
        const children = Array.isArray(option.children) ? option.children : [];

        children.forEach((column) => {
          prev[column.key] = column;
        });
        return prev;
      }, {});
    },

    getEmptySelectedOrderKeys() {
      return {
        dimension: [],
        metric: [],
      };
    },

    normalizeColumnType(type) {
      return type === 'metric' ? 'metric' : 'dimension';
    },

    getSelectedColumns(source = []) {
      let flatIndex = 0;

      return (Array.isArray(source) ? source : []).reduce((prev, option) => {
        const children = Array.isArray(option.children) ? option.children : [];
        children.forEach((column) => {
          if (!column.isSelected) {
            flatIndex += 1;
            return;
          }

          const type = this.normalizeColumnType(column.type || option.type);

          prev.push({
            ...cloneDeep(column),
            key: column.key,
            name: column.name,
            type,
            groupKey: option.key,
            groupName: option.name,
            selectedOrder: Number.isFinite(Number(column.selectedOrder))
              ? Number(column.selectedOrder)
              : null,
            flatIndex,
          });
          flatIndex += 1;
        });
        return prev;
      }, []);
    },

    getInitialSelectedOrderKeys(source = []) {
      const selectedColumns = this.getSelectedColumns(source);

      return this.selectedTypes.reduce((prev, type) => {
        prev[type] = selectedColumns
          .filter((item) => item.type === type)
          .sort((a, b) => {
            const aOrder =
              a.selectedOrder === null ? MAX_ORDER : Number(a.selectedOrder);
            const bOrder =
              b.selectedOrder === null ? MAX_ORDER : Number(b.selectedOrder);

            if (aOrder !== bOrder) {
              return aOrder - bOrder;
            }

            return a.flatIndex - b.flatIndex;
          })
          .map((item) => item.key);

        return prev;
      }, this.getEmptySelectedOrderKeys());
    },

    syncSelectedOrderKeys() {
      const currentSelectedKeysByType = this.getEmptySelectedOrderKeys();

      this.getSelectedColumns(this.options).forEach((item) => {
        const type = this.normalizeColumnType(item.type);
        currentSelectedKeysByType[type].push(item.key);
      });

      const nextSelectedOrderKeys = this.getEmptySelectedOrderKeys();

      this.selectedTypes.forEach((type) => {
        const currentKeys = currentSelectedKeysByType[type];
        const preserved = (
          this.selectedOrderKeysByType[type] || []
        ).filter((key) => currentKeys.includes(key));
        const additions = currentKeys.filter((key) => !preserved.includes(key));

        nextSelectedOrderKeys[type] = preserved.concat(additions);
      });

      this.selectedOrderKeysByType = nextSelectedOrderKeys;
    },

    handleClose() {
      this.$emit('update:visible', false);
    },

    handleCheckAllChange(val, option) {
      option.isIndeterminate = false;
      option.children = (option.children || []).map((item) => {
        item.isSelected = val;
        return item;
      });
      this.syncSelectedOrderKeys();
    },

    handleChange(val, col, parent) {
      col.isSelected = val;
      parent.isCheckAll = this.isCheckAll(parent.children || []);
      parent.isIndeterminate = this.isIndeterminate(parent.children || []);
      this.syncSelectedOrderKeys();
    },

    isCheckAll(list) {
      const length = list.length;
      const selectedLength = list.filter((item) => item.isSelected).length;

      return selectedLength === length && length > 0;
    },

    isIndeterminate(list) {
      const length = list.length;
      const selectedLength = list.filter((item) => item.isSelected).length;

      return selectedLength < length && selectedLength > 0;
    },

    handleSelectedDragStart(event, column) {
      this.draggingKey = column.key;
      this.draggingType = this.normalizeColumnType(column.type);
      this.dragOverKey = column.key;
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', column.key);
      }
    },

    handleSelectedDragOver(event, column) {
      if (!this.draggingKey) return;

      if (this.normalizeColumnType(column.type) !== this.draggingType) {
        this.dragOverKey = '';
        if (event.dataTransfer) {
          event.dataTransfer.dropEffect = 'none';
        }
        return;
      }

      event.preventDefault();
      this.dragOverKey = column.key;
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'move';
      }
    },

    handleSelectedDrop(event, column) {
      if (
        !this.draggingKey ||
        this.draggingKey === column.key ||
        this.normalizeColumnType(column.type) !== this.draggingType
      ) {
        this.handleSelectedDragEnd();
        return;
      }

      event.preventDefault();

      const nextOrderKeys = [
        ...this.selectedOrderKeysByType[this.draggingType],
      ];
      const fromIndex = nextOrderKeys.indexOf(this.draggingKey);
      const targetIndex = nextOrderKeys.indexOf(column.key);

      if (fromIndex === -1 || targetIndex === -1) {
        this.handleSelectedDragEnd();
        return;
      }

      const [movedKey] = nextOrderKeys.splice(fromIndex, 1);
      let toIndex = targetIndex;
      if (fromIndex < targetIndex) {
        toIndex -= 1;
      }

      if (this.getDragPosition(event) === 'after') {
        toIndex += 1;
      }

      nextOrderKeys.splice(toIndex, 0, movedKey);
      this.selectedOrderKeysByType = {
        ...this.selectedOrderKeysByType,
        [this.draggingType]: nextOrderKeys,
      };
      this.handleSelectedDragEnd();
    },

    handleSelectedContainerDragOver(type, event) {
      if (
        !this.draggingKey ||
        this.normalizeColumnType(type) !== this.draggingType
      ) {
        this.dragOverKey = '';
        if (event.dataTransfer) {
          event.dataTransfer.dropEffect = 'none';
        }
        return;
      }

      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'move';
      }
    },

    handleSelectedContainerDrop(type, event) {
      if (
        !this.draggingKey ||
        this.normalizeColumnType(type) !== this.draggingType
      ) {
        this.handleSelectedDragEnd();
        return;
      }

      event.preventDefault();

      const nextOrderKeys = this.selectedOrderKeysByType[
        this.draggingType
      ].filter((key) => key !== this.draggingKey);

      nextOrderKeys.push(this.draggingKey);
      this.selectedOrderKeysByType = {
        ...this.selectedOrderKeysByType,
        [this.draggingType]: nextOrderKeys,
      };
      this.handleSelectedDragEnd();
    },

    handleSelectedDragEnd() {
      this.draggingKey = '';
      this.draggingType = '';
      this.dragOverKey = '';
    },

    handleClearSelected() {
      this.options = this.options.map((option) => {
        option.children = (option.children || []).map((column) => {
          column.isSelected = false;
          return column;
        });
        this.updateGroupCheckState(option);
        return option;
      });
      this.selectedOrderKeysByType = this.getEmptySelectedOrderKeys();
      this.handleSelectedDragEnd();
    },

    handleRemoveSelected(targetColumn) {
      this.options = this.options.map((option) => {
        let hasRemoved = false;

        option.children = (option.children || []).map((column) => {
          if (column.key === targetColumn.key) {
            column.isSelected = false;
            hasRemoved = true;
          }
          return column;
        });

        if (hasRemoved) {
          this.updateGroupCheckState(option);
        }

        return option;
      });
      this.syncSelectedOrderKeys();

      if (
        this.draggingKey === targetColumn.key ||
        this.dragOverKey === targetColumn.key
      ) {
        this.handleSelectedDragEnd();
      }
    },

    getDragPosition(event) {
      const rect = event.currentTarget.getBoundingClientRect();
      return event.clientY - rect.top > rect.height / 2 ? 'after' : 'before';
    },

    buildSubmitOptions() {
      return this.selectedColumns.map((column, index) => {
        const selectedColumn = cloneDeep(column);

        delete selectedColumn.flatIndex;
        selectedColumn.isSelected = true;
        selectedColumn.selectedOrder = index;

        return selectedColumn;
      });
    },

    handleCancel() {
      this.resetOptions(this.dataSource);
      this.handleClose();
    },

    handleConfirm() {
      const options = this.buildSubmitOptions();
      this.handleClose();
      this.$emit('change', options);
    },
  },
};
</script>

<style lang="less" scoped>
.dialog-body {
  display: flex;
  align-items: stretch;
  gap: 20px;
  min-height: 420px;
}

.option-panel {
  flex: 1;
  min-width: 0;
  max-height: 60vh;
  padding-right: 20px;
  overflow-y: auto;
  border-right: 1px solid #ebeef5;
}

.selected-panel {
  width: 300px;
  display: flex;
  flex-direction: column;
  min-width: 0;
  max-height: 60vh;
}

.selected-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.selected-panel__summary {
  color: #303133;
  font-size: 14px;
  font-weight: 700;
}

.selected-panel__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  margin-left: 8px;
  padding: 0 8px;
  border-radius: 12px;
  background: #f0f6ff;
  color: #409eff;
  font-size: 13px;
}

.selected-panel__clear {
  padding: 0;
}

.selected-panel__content {
  flex: 1;
  min-height: 0;
  max-height: 60vh;
  padding: 12px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fafafa;
}

.selected-group + .selected-group {
  margin-top: 12px;
}

.selected-group {
  padding: 12px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fff;
}

.selected-group__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.selected-group__title {
  color: #303133;
  font-size: 13px;
  font-weight: 700;
}

.selected-group__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 8px;
  border-radius: 12px;
  background: #f5f7fa;
  color: #909399;
  font-size: 12px;
}

.selected-group__body {
  min-height: 96px;
}

.selected-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 160px;
  color: #909399;
  font-size: 13px;
}

.selected-empty--group {
  min-height: 96px;
  pointer-events: none;
}

.selected-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: #fff;
  cursor: move;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.selected-item + .selected-item {
  margin-top: 10px;
}

.selected-item.is-dragging {
  opacity: 0.6;
}

.selected-item.is-drag-over {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.12);
}

.selected-item__drag {
  color: #c0c4cc;
  font-size: 18px;
  line-height: 1;
  letter-spacing: -2px;
  user-select: none;
}

.selected-item__text {
  min-width: 0;
  flex: 1;
}

.selected-item__remove {
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: #999;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
}

.selected-item__remove:hover {
  color: #dd6161;
}

.selected-item__name {
  color: #303133;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  word-break: break-word;
}

.selected-item__group {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
  line-height: 1.4;
  word-break: break-word;
}

.group {
  margin-bottom: 32px;
}

.group-name {
  font-size: 16px;
  font-weight: bold;
  color: #333;
}

.group-divider {
  height: 20px;
}

.checkbox {
  display: block;
  margin-bottom: 20px;
}

.footer {
  display: flex;
  justify-content: center;
}

@media (max-width: 1200px) {
  .dialog-body {
    flex-direction: column;
  }

  .option-panel {
    max-height: none;
    padding-right: 0;
    border-right: 0;
  }

  .selected-panel {
    width: 100%;
  }

  .selected-panel__content {
    max-height: 280px;
  }
}
</style>
